/**
 * 预计可赎回：term>=7 的已确认买入可存 expectedRedeemYmd。
 * 默认按确认日+term 自然日预估；用户校正后 expectedRedeemSource='user' 不再自动覆盖。
 * 收益详情汇总展示 FIFO 剩余批次中最近的可赎日。
 */
import {
  EPS,
  isBuyTrade,
  isTradeConfirmed,
  normYmd,
  sortTrades,
  tradeAccrualDate,
  tradeShares,
} from './assetHoldingCalc';
import { todayYMD } from './assetTradingCalendar';

export const MIN_TERM_DAYS = 7;

function addDaysYmd(ymd, deltaDays) {
  const d = normYmd(ymd);
  if (!d) return '';
  const n = Number(deltaDays);
  if (!Number.isFinite(n) || n === 0) return d;
  try {
    const dt = new Date(d.replace(/-/g, '/'));
    dt.setDate(dt.getDate() + n);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  } catch (_) {
    return d;
  }
}

export function todayYmdLocal() {
  return todayYMD() || (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
}

export function daysUntil(fromYmd, toYmd) {
  const a = normYmd(fromYmd);
  const b = normYmd(toYmd);
  if (!a || !b) return null;
  try {
    const ms = new Date(b.replace(/-/g, '/')).getTime() - new Date(a.replace(/-/g, '/')).getTime();
    if (!Number.isFinite(ms)) return null;
    return Math.floor(ms / (24 * 3600 * 1000));
  } catch (_) {
    return null;
  }
}

function round6(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round(v * 1e6) / 1e6;
}

export function parseTermDays(termRaw) {
  const n = parseInt(termRaw, 10);
  if (!Number.isFinite(n) || n < MIN_TERM_DAYS) return null;
  return n;
}

/** 从产品 meta / 持仓对象取 term（Web 无小程序 product-cache） */
export function getTermDaysForProduct(productOrTerm) {
  if (productOrTerm == null) return null;
  if (typeof productOrTerm === 'number' || typeof productOrTerm === 'string') {
    return parseTermDays(productOrTerm);
  }
  const term = productOrTerm.term != null ? productOrTerm.term : productOrTerm.termDays;
  return parseTermDays(term);
}

export function isRedeemHintProduct(productOrTerm) {
  return !!getTermDaysForProduct(productOrTerm);
}

export function isConfirmedBuyTrade(trade) {
  return !!(trade && isBuyTrade(trade) && isTradeConfirmed(trade));
}

/** 持有期起点：优先 confirmDate，缺失时 fallback 起息日 */
export function holdStartForBuy(trade) {
  const confirm = normYmd(trade && trade.confirmDate);
  if (confirm) return confirm;
  return tradeAccrualDate(trade);
}

function tradeSharesOf(trade) {
  if (trade && trade.shares != null) {
    const sh = Number(trade.shares);
    if (Number.isFinite(sh) && sh > 0) return round6(sh);
  }
  return tradeShares(trade);
}

/** 系统预估：确认日 + term 自然日 */
export function computeAutoExpectedRedeemYmd(trade, termDays) {
  const days = parseTermDays(termDays);
  if (!days || !isConfirmedBuyTrade(trade)) return '';
  const holdStart = holdStartForBuy(trade);
  return holdStart ? addDaysYmd(holdStart, days) : '';
}

/** 读取单笔预计可赎日：user 优先，否则 auto 字段或现场计算 */
export function getExpectedRedeemYmdForTrade(trade, termDays) {
  const days = parseTermDays(termDays);
  if (!isConfirmedBuyTrade(trade) || !days) return '';
  if (trade.expectedRedeemSource === 'user') {
    return normYmd(trade.expectedRedeemYmd) || '';
  }
  const stored = normYmd(trade.expectedRedeemYmd);
  if (stored) return stored;
  return computeAutoExpectedRedeemYmd(trade, days);
}

export function isUserExpectedRedeem(trade) {
  return !!(trade && trade.expectedRedeemSource === 'user' && normYmd(trade.expectedRedeemYmd));
}

/** 写入/刷新 auto 预估（不覆盖 user） */
export function ensureAutoExpectedRedeemOnTrade(trade, termDays) {
  const days = parseTermDays(termDays);
  if (!isConfirmedBuyTrade(trade) || !days) return trade;
  if (isUserExpectedRedeem(trade)) return trade;
  const autoYmd = computeAutoExpectedRedeemYmd(trade, days);
  if (!autoYmd) return trade;
  const cur = normYmd(trade.expectedRedeemYmd);
  if (cur === autoYmd && trade.expectedRedeemSource === 'auto') return trade;
  return {
    ...trade,
    expectedRedeemYmd: autoYmd,
    expectedRedeemSource: 'auto',
  };
}

/** 恢复为系统预估（本轮 UI 未挂，供将来扩展） */
export function restoreAutoExpectedRedeemOnTrade(trade, termDays) {
  const days = parseTermDays(termDays);
  if (!isConfirmedBuyTrade(trade) || !days) return trade;
  const autoYmd = computeAutoExpectedRedeemYmd(trade, days);
  if (!autoYmd) {
    const next = { ...trade };
    delete next.expectedRedeemYmd;
    delete next.expectedRedeemSource;
    return next;
  }
  return {
    ...trade,
    expectedRedeemYmd: autoYmd,
    expectedRedeemSource: 'auto',
  };
}

/**
 * FIFO 模拟剩余买入批次（含原 trade 引用）
 * @returns {Array<{ remainingShares: number, holdStart: string, trade: object }>}
 */
export function simulateFifoRemainingLots(trades) {
  const sorted = sortTrades(Array.isArray(trades) ? trades : []);
  const lots = [];

  for (let i = 0; i < sorted.length; i++) {
    const t = sorted[i];
    if (!isTradeConfirmed(t)) continue;
    const sh = tradeSharesOf(t);
    if (!(sh > EPS)) continue;

    if (isBuyTrade(t)) {
      const holdStart = holdStartForBuy(t);
      if (!holdStart) continue;
      lots.push({ remainingShares: sh, holdStart, trade: t });
      continue;
    }

    let toRedeem = sh;
    while (toRedeem > EPS && lots.length > 0) {
      const lot = lots[0];
      const take = Math.min(toRedeem, lot.remainingShares);
      lot.remainingShares = round6(lot.remainingShares - take);
      toRedeem = round6(toRedeem - take);
      if (lot.remainingShares <= EPS) lots.shift();
    }
  }

  return lots.filter((lot) => lot.remainingShares > EPS);
}

/** 详情展示：去掉年份，如 2026-11-16 → 11-16 */
export function formatCompactYmd(ymd) {
  const d = normYmd(ymd);
  if (!d) return '';
  return `${d.slice(5, 7)}-${d.slice(8, 10)}`;
}

function formatDaysSuffix(ymd, today) {
  if (!ymd || ymd < today) return '';
  const left = daysUntil(today, ymd);
  if (left === 0) return '（今日可赎）';
  if (left != null && left > 0) return `（还有${left}天）`;
  return '';
}

/**
 * @returns {{ visible: boolean, text: string, isAuto: boolean }}
 */
export function summarizeRedeemHint(opts) {
  const o = opts && typeof opts === 'object' ? opts : {};
  const trades = Array.isArray(o.trades) ? o.trades : [];
  const termDays = o.termDays != null
    ? parseTermDays(o.termDays)
    : getTermDaysForProduct(o.product || o);
  const today = normYmd(o.todayYmd) || todayYmdLocal();

  if (!termDays) return { visible: false, text: '', isAuto: false };

  const lots = simulateFifoRemainingLots(trades);
  if (!lots.length) return { visible: false, text: '', isAuto: false };

  const lotDates = lots
    .map((lot) => ({
      trade: lot.trade,
      ymd: getExpectedRedeemYmdForTrade(lot.trade, termDays),
      isAuto: !isUserExpectedRedeem(lot.trade),
    }))
    .filter((x) => !!x.ymd);

  if (!lotDates.length) return { visible: false, text: '', isAuto: false };

  const past = lotDates.filter((x) => x.ymd < today);
  const future = lotDates.filter((x) => x.ymd >= today).sort((a, b) => a.ymd.localeCompare(b.ymd));

  if (future.length) {
    const next = future[0];
    const datePart = formatCompactYmd(next.ymd);
    const suffix = formatDaysSuffix(next.ymd, today);
    if (past.length) {
      return {
        visible: true,
        text: `当前可赎 · 下一批：${datePart}${suffix}`,
        isAuto: !!next.isAuto,
      };
    }
    return {
      visible: true,
      text: `最早可赎回日：${datePart}${suffix}`,
      isAuto: !!next.isAuto,
    };
  }

  return { visible: true, text: '最早可赎回日：当前已有份额可赎', isAuto: false };
}
